import "./style.scss"; // Import custom styles
import "../../../assets/css/CountryCode.scss"; // Import country code styles

import {
  Box,
  Button,
  Container,
  CssBaseline,
  Grid,
  Paper,
  TextField,
  Typography,
  Select,
  MenuItem,
} from "@material-ui/core"; // Material-UI components
import InputAdornment from "@material-ui/core/InputAdornment"; // Input adornment for text fields
import PropTypes from "prop-types"; // PropTypes for type checking

import { connect } from "react-redux"; // Redux connect for state management
import { useFormik } from "formik"; // Formik for form handling
import { useHistory } from "react-router-dom"; // React Router for navigation
import * as yup from "yup"; // Yup for validation schema
import { useTheme } from "@material-ui/core/styles"; // Material-UI theme

import useMediaQuery from "@material-ui/core/useMediaQuery"; // Media query hook for responsive design

import { useSnackbar } from "notistack"; // Snackbar for notifications
import React from "react"; // React library
import isEmpty from "lodash/isEmpty"; // Lodash utility for checking empty objects
import { getCountryList } from "../../../actions/login.action"; // Redux action for fetching country list
import { confirmForgetPassword } from "../../../actions/Operations"; // Redux action for confirming forget password
import useStyles from "./Style"; // Custom styles

function ResetPassword(props) {
  const classes = useStyles(); // Initialize custom styles
  const history = useHistory(); // React Router history for navigation
  const { enqueueSnackbar } = useSnackbar(); // Snackbar for notifications
  const theme = useTheme(); // Material-UI theme
  const version = process.env.REACT_APP_BUILD_VERSION_NUMBER; // App version
  const windowWidth = useMediaQuery(theme.breakpoints.up("md")); // Check if the screen width is medium or larger
  const emailRegEx = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}))$/; // Regular expression for email validation
  const [countryCode, setCountryCode] = React.useState([]); // State for country codes
  const path = "/"; // Frontegg hosted login is the only login path

  // Validation schema for the form
  const validationSchema = yup.object({
    email: yup
      .string("Please enter a valid email address.")
      .required("Email address is required.")
      .test(
        "id",
        "Please enter a valid email address.",
        value => !value
          || emailRegEx.test(value)
          || /^(\+\d{1,3}[- ]?)?\d{10}$/.test(parseInt(value))
      )
      .trim(),
  });

  // Function to handle form submission
  const onSubmit = values => {
    const payload = { email: values.email };
    if (/^\d+$/.test(values.email)) {
      payload.countryCode = values.countryCode;
    }
    props.confirmForgetPassword(
      payload,
      data => {
        if (data.status) {
          enqueueSnackbar(data.message, {
            variant: "success",
          });
        } else {
          data.errors.forEach(x => {
            if (x.location === "body") {
              formik.setFieldError(x.param, x.msg);
            } else {
              enqueueSnackbar(x.msg, {
                variant: "error",
              });
            }
          });
        }
      },
      () => {
        enqueueSnackbar("Request could not be completed", {
          variant: "error",
        });
      }
    );
  };

  // Initialize Formik for form handling
  const formik = useFormik({
    initialValues: {
      email: "",
      countryCode: 1,
    },
    validationSchema,
    onSubmit,
  });

  // Effect to fetch country list if not already available
  React.useEffect(() => {
    if (isEmpty(countryCode)) {
      props.getCountryList(data => {
        setCountryCode(data.Country_Data);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dashboardTiles" style={{ position: "relative" }}>
      <Container component="main" className={classes.rootNew}>
        <CssBaseline />
        <Paper
          className={!windowWidth ? classes.paperNewM : classes.paperNew}
          elevation={5}
        >
          {/* Left panel for branding */}
          <Box
            height="100%"
            p={{
              xs: 3,
              sm: 4,
              md: 5,
              xl: 6,
            }}
            display={{ xs: "none", sm: "none", md: "flex" }}
            className={`${classes.panelWidth} auth-left-panel`}
          >
            <Box
              display="flex"
              flexDirection="column"
              height="100%"
              width="100%"
              justifyContent="center"
              alignItems="center"
            >
              <div className={classes.brandLogo}>
                <img
                  className="imgf"
                  src={
                    props.app.defaultTheme
                    && props.app.defaultTheme.data[0].Logo_Image
                  }
                  alt="appName"
                />
              </div>
              <Box mt={2} className={classes.brandTag}>
                <Typography component="h6" variant="subtitle1" align="center" />
              </Box>
            </Box>
          </Box>
          <Box
            display={{ xs: "flex", sm: "flex", md: "none" }}
            className="mobileHeader"
            container
            justify="center"
          >
            <div className={classes.brandLogo}>
              <img
                className="imgm"
                src={
                  props.app.defaultTheme
                  && props.app.defaultTheme.data[0].Logo_Image
                }
                alt="appName"
              />
            </div>
          </Box>
          <Box
            height="100%"
            pt={4}
            pb={4}
            pl={{
              xs: 4,
              sm: 6,
              md: 2,
              xl: 8,
            }}
            pr={{
              xs: 4,
              sm: 6,
              md: 2,
              xl: 8,
            }}
            display="flex"
            alignItems="center"
            className={
              !windowWidth
                ? `${classes.panelWidth} auth-right-panelM`
                : `${classes.panelWidth} auth-right-panel`
            }
          >
            <div className={classes.authInnerForm}>
              <Typography component="p" variant="subtitle2">
                <strong>Forgot Password</strong>
              </Typography>

              <Typography
                style={{ marginBottom: "15px", marginTop: "40px" }}
                component="p"
                variant="caption"
                gutterBottom
              >
                Enter your email address to receive a password reset link.
              </Typography>
              <form onSubmit={formik.handleSubmit}>
                <div className={classes.form}>
                  <Grid
                    container
                    alignItems="center"
                    justify="center"
                    className={classes.spaceTop}
                  >
                    {/* Email input field */}
                    <TextField
                      as={TextField}
                      labelText="Email..."
                      id="email"
                      name="email"
                      required
                      fullWidth
                      label="Enter Email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      error={Boolean(formik.errors.email)}
                      formControlProps={{
                        fullWidth: true,
                      }}
                      InputProps={
                        /^\d+$/.test(formik.values.email) && {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Select
                                id="country-menu"
                                value={formik.values.countryCode}
                                onChange={event => {
                                  formik.setFieldValue(
                                    "countryCode",
                                    event.target.value
                                  );
                                }}
                                classes={{ root: "country-list" }}
                                className="no-underline"
                                inputProps={{
                                  "aria-label": "Without label",
                                }}
                              >
                                {countryCode.map(country => (
                                  <MenuItem
                                    value={country.Dial_Code}
                                    option={countryCode}
                                    key={country.Country_Code}
                                    className="country"
                                  >
                                    <span
                                      className={`country-flag ${country.Country_Code.toLowerCase().substring(
                                        0,
                                        2
                                      )}`}
                                    />
                                    &nbsp;
                                    <span className="dial-code">{`+${country.Dial_Code}`}</span>
                                    &nbsp;
                                    <span className="country-name">
                                      (
                                      {country.Country_Name}
                                      )
                                    </span>
                                  </MenuItem>
                                ))}
                              </Select>
                            </InputAdornment>
                          ),
                        }
                      }
                    />

                    <Typography
                      component="span"
                      className="error"
                      variant="body2"
                    >
                      {formik.errors.email}
                    </Typography>
                  </Grid>

                  {/* Submit button */}
                  <Grid
                    container
                    alignItems="center"
                    justify="center"
                    style={{ marginTop: "20px" }}
                    className={classes.spaceTop}
                  >
                    <Grid sm={10} xs={10} item>
                      <Button
                        size="large"
                        fullWidth
                        style={{ marginBottom: "10px" }}
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={!isEmpty(formik.errors) || !formik.dirty}
                        className={classes.submit}
                      >
                        Send
                      </Button>
                    </Grid>
                  </Grid>
                  {/* Return to login button */}
                  <Grid
                    container
                    alignItems="center"
                    justify="center"
                    className={classes.spaceTop}
                  >
                    <Grid item>
                      <Button onClick={() => history.push(path)} color="primary">
                        <b>Return To Login</b>
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              </form>
            </div>
          </Box>
        </Paper>
      </Container>
      {/* Footer with version and branding */}
      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 2rem",
          position: "absolute",
          bottom: "2rem",
          color: "#e5e1e261",
          fontSize: "12px",
        }}
      >
        <i>
          Version:
          {version}
        </i>
        <i
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "5px",
          }}
        >
          <div>Powered By:</div>

          <img
            style={{
              width: "154px",
            }}
            className="imgf"
            src="/images/tsc_logo.png"
            alt="appName"
          />
        </i>
      </div>
    </div>
  );
}

ResetPassword.propTypes = {
  confirmForgetPassword: PropTypes.func, // Redux action to confirm forget password
  getCountryList: PropTypes.func, // Redux action to fetch country list
  app: PropTypes.object, // App state from Redux
};

// Map Redux state to component props
const mapStateToProps = ({ app }) => ({
  userData: app.userDetails, // Map user details from Redux state
  app,
});

// Connect component to Redux and export
export default connect(mapStateToProps, {
  confirmForgetPassword,
  getCountryList,
})(ResetPassword);
